import './main.css';
import { useContext, useEffect } from 'react';
import { clsx } from 'keycloakify/tools/clsx';
import type { TemplateProps } from 'keycloakify/login/TemplateProps';
import { getKcClsx } from 'keycloakify/login/lib/kcClsx';
import { useSetClassName } from 'keycloakify/tools/useSetClassName';
import Text from '@bankiru/ui-kit/core/Text';
import { Alert } from '@bankiru/ui-kit/components/Alert';
import type { I18n } from './i18n';
import type { KcContext } from './KcContext';
import LogoImg from '../img/logo.svg';
import { useInitialize } from 'keycloakify/login/Template.useInitialize';
import { ThemeContext } from '../context/ThemeContextWrapper';
import { Indent } from '@bankiru/ui-kit/components/Indent';

export default function Template(props: TemplateProps<KcContext, I18n>) {
	const {
		displayInfo = false,
		displayMessage = true,
		displayRequiredFields = false,
		headerNode,
		socialProvidersNode = null,
		infoNode = null,
		documentTitle,
		bodyClassName,
		kcContext,
		i18n,
		doUseDefaultCss,
		classes,
		children,
	} = props;

	const { kcClsx } = getKcClsx({ doUseDefaultCss, classes });

	const { isMobile } = useContext(ThemeContext);

	const { msg, msgStr } = i18n;

	const { auth, url, message, isAppInitiatedAction } = kcContext;

	useEffect(() => {
		document.title = documentTitle ?? msgStr('loginTitle', kcContext.realm.displayName);
	}, [documentTitle, kcContext.realm.displayName, msgStr]);

	useSetClassName({
		qualifiedName: 'html',
		className: kcClsx('kcHtmlClass'),
	});

	useSetClassName({
		qualifiedName: 'body',
		className: bodyClassName ?? kcClsx('kcBodyClass'),
	});

	const { isReadyToRender } = useInitialize({ kcContext, doUseDefaultCss });

	if (!isReadyToRender) {
		return null;
	}

	return (
		<div className={kcClsx('kcLoginClass')}>
			<div id="kc-header" className={kcClsx('kcHeaderClass')}>
				<div id="kc-header-wrapper" className={kcClsx('kcHeaderWrapperClass')}>
					<img src={LogoImg} width={168} height={32} alt="Банки.ру" />
				</div>
			</div>

			<div className={kcClsx('kcFormCardClass')}>
				<header className={kcClsx('kcFormHeaderClass')}>
					{(() => {
						const node =
							!(
								auth !== undefined &&
								auth.showUsername &&
								!auth.showResetCredentials
							) || headerNode ? (
								<>
									<Text
										color="primary"
										size={isMobile ? 'fs18' : 'fs24'}
										tagName="span"
										weight="medium"
									>
										{headerNode}
									</Text>
								</>
							) : (
								<div id="kc-username" className={kcClsx('kcFormGroupClass')}>
									<label id="kc-attempted-username">
										{auth.attemptedUsername}
									</label>
									<a
										id="reset-login"
										href={url.loginRestartFlowUrl}
										aria-label={msgStr('restartLoginTooltip')}
									>
										<div className="kc-login-tooltip">
											<i className={kcClsx('kcResetFlowIcon')}></i>
											<span className="kc-tooltip-text">
												{msg('restartLoginTooltip')}
											</span>
										</div>
									</a>
								</div>
							);

						if (displayRequiredFields) {
							return (
								<div className={kcClsx('kcContentWrapperClass')}>
									<div
										className={clsx(kcClsx('kcLabelWrapperClass'), 'subtitle')}
									>
										<span className="subtitle">
											<span className="required">*</span>
											{msg('requiredFields')}
										</span>
									</div>
									<div className="col-md-10">{node}</div>
								</div>
							);
						}

						return node;
					})()}
				</header>
				<div id="kc-content">
					<div id="kc-content-wrapper">
						{/* App-initiated actions should not see warning messages about the need to complete the action during login. */}
						{displayMessage &&
							message !== undefined &&
							(message.type !== 'warning' || !isAppInitiatedAction) && (
								<Indent margin="8 0">
									<div
										className={clsx(
											`alert-${message.type}`,
											kcClsx('kcAlertClass'),
											`pf-m-${
												message?.type === 'error' ? 'danger' : message.type
											}`,
										)}
									>
										<Alert type={message.type} content={message.summary} />
									</div>
								</Indent>
							)}
						{children}
						{auth !== undefined && auth.showTryAnotherWayLink && (
							<form
								id="kc-select-try-another-way-form"
								action={url.loginAction}
								method="post"
							>
								<div className={kcClsx('kcFormGroupClass')}>
									<div className={kcClsx('kcFormGroupClass')}>
										<input type="hidden" name="tryAnotherWay" value="on" />
										<a
											href="#"
											id="try-another-way"
											onClick={() => {
												document.forms[
													'kc-select-try-another-way-form' as never
												].submit();
												return false;
											}}
										>
											{msg('doTryAnotherWay')}
										</a>
									</div>
								</div>
							</form>
						)}
						{socialProvidersNode}
						{displayInfo && (
							<div id="kc-info" className={kcClsx('kcSignUpClass')}>
								<div
									id="kc-info-wrapper"
									className={kcClsx('kcInfoAreaWrapperClass')}
								>
									{infoNode}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
